import { z } from 'zod';

import { FlowNodeRetrySchema } from '../schemas/flow-graph.schema';
import { LibraryPromptPinSchema } from '../schemas/node-config.schemas';
import { PromptMessagesSchema } from '../services/flow-prompt';

// agents-architecture §3.1: the limits of the schema, and the platform's own defaults.
export const AGENT_LIMITS = {
  maxSteps: 100,
  defaultSteps: 12,
  minCostUsd: 0.01,
  maxCostUsd: 50,
  defaultCostUsd: 0.25,
  minActiveSec: 10,
  maxActiveSec: 1800,
  defaultActiveSec: 120,
  maxTools: 30,
  maxToolFields: 30,
  maxOutputFields: 30,
  maxCategories: 20,
  maxParallelTools: 10,
  defaultToolResultChars: 16_000,
  maxToolResultBytes: 1_048_576,
  maxRunFlowWaitSec: 600,
  defaultRunFlowWaitSec: 60,
  defaultJobWaitSec: 600,
  maxJobWaitSec: 3600,
  defaultMaxCopies: 2000,
  maxCopies: 10_000,
  maxAgentDepth: 3,
  maxCorrections: 1,
} as const;

export const AGENT_TOOL_NAME_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
export const FINAL_ANSWER_TOOL = 'final_answer';

/** Phase 1 runs these four; search_events, search_knowledge, mcp and human come later (§18). */
export const AGENT_TOOL_KINDS = ['action', 'code', 'run_flow', 'hookpipe'] as const;
export type TAgentToolKind = (typeof AGENT_TOOL_KINDS)[number];

export const HOOKPIPE_ACTIONS = [
  'redeliver_batch',
  'mute_issue',
  'search_issues',
  'read_issue',
  'search_runs',
  'read_run',
] as const;
export type THookpipeAction = (typeof HOOKPIPE_ACTIONS)[number];

export const AGENT_FIELD_TYPES = [
  'string',
  'number',
  'integer',
  'boolean',
  'object',
  'array',
] as const;
export type TAgentFieldType = (typeof AGENT_FIELD_TYPES)[number];

const id = z.string().trim().min(1).max(32);
const template = z.string().max(20_000);
const toolKey = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, 'lowercase letters, digits and _');
const fieldName = z
  .string()
  .regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/, 'letters, digits and _, not starting with a digit');

// `fill: agent` — the model gives it, checked against this; `fixed` — a template on the copy.
export const AgentToolFieldSchema = z.object({
  path: fieldName,
  fill: z.enum(['agent', 'fixed']).default('agent'),
  type: z.enum(AGENT_FIELD_TYPES).default('string'),
  required: z.boolean().default(false),
  description: z.string().max(1000).default(''),
  enum: z.array(z.string().max(200)).max(50).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  value: template.optional(),
});

const fields = z.array(AgentToolFieldSchema).max(AGENT_LIMITS.maxToolFields).default([]);

const common = {
  key: toolKey,
  name: z.string().regex(AGENT_TOOL_NAME_PATTERN, 'lowercase letters, digits and _, up to 64'),
  description: z.string().trim().min(1).max(2000),
  fields,
  mode: z.enum(['auto', 'ask']).default('auto'),
  changesData: z.boolean().optional(),
  retry: FlowNodeRetrySchema.optional(),
};

export const AgentActionToolSchema = z.object({
  ...common,
  kind: z.literal('action'),
  config: z.object({
    connector: z.string().trim().min(1).max(40),
    action: z.string().trim().min(1).max(60),
    connectorVersion: z.number().int().positive(),
    credentialId: id.nullable().default(null),
    input: z.record(z.string(), z.unknown()).default({}),
  }),
});

export const AgentCodeToolSchema = z.object({
  ...common,
  kind: z.literal('code'),
  config: z.object({ itemId: id, versionId: id, version: z.number().int().positive() }),
});

export const AgentRunFlowToolSchema = z.object({
  ...common,
  kind: z.literal('run_flow'),
  config: z.object({
    flowId: id,
    waitSec: z
      .number()
      .int()
      .min(1)
      .max(AGENT_LIMITS.maxRunFlowWaitSec)
      .default(AGENT_LIMITS.defaultRunFlowWaitSec),
  }),
});

export const AgentHookpipeToolSchema = z.object({
  ...common,
  kind: z.literal('hookpipe'),
  config: z.object({
    action: z.enum(HOOKPIPE_ACTIONS),
    // "Flows it may touch": outside the list a flow does not exist for the agent (§7.5).
    flowIds: z.array(id).max(50).default([]),
    maxCopies: z
      .number()
      .int()
      .positive()
      .max(AGENT_LIMITS.maxCopies)
      .default(AGENT_LIMITS.defaultMaxCopies),
    waitSec: z
      .number()
      .int()
      .min(1)
      .max(AGENT_LIMITS.maxJobWaitSec)
      .default(AGENT_LIMITS.defaultJobWaitSec),
  }),
});

export const AgentToolSchema = z.discriminatedUnion('kind', [
  AgentActionToolSchema,
  AgentCodeToolSchema,
  AgentRunFlowToolSchema,
  AgentHookpipeToolSchema,
]);

export const AgentOutputFieldSchema = z.object({
  name: fieldName,
  type: z.enum(['string', 'number', 'integer', 'boolean', 'list', 'object']).default('string'),
  description: z.string().max(1000).default(''),
  required: z.boolean().default(true),
  min: z.number().optional(),
  max: z.number().optional(),
  enum: z.array(z.string().max(200)).max(50).optional(),
});

export const AgentOutputSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text') }),
  z.object({
    kind: z.literal('json'),
    fields: z.array(AgentOutputFieldSchema).min(1).max(AGENT_LIMITS.maxOutputFields),
  }),
  z.object({
    kind: z.literal('categories'),
    categories: z
      .array(
        z.object({
          name: z.string().regex(/^[a-z0-9][a-z0-9_]{0,31}$/, 'lowercase letters, digits and _'),
          description: z.string().max(500).default(''),
        }),
      )
      .min(2)
      .max(AGENT_LIMITS.maxCategories),
  }),
]);

// Only `off` runs in phase 1; the conversation shape is here so drafts keep it (§5.7).
export const AgentMemorySchema = z.object({
  mode: z.enum(['off', 'conversation']).default('off'),
  key: template.optional(),
  label: template.optional(),
  keep: z
    .object({ by: z.enum(['messages', 'tokens']), count: z.number().int().positive().max(100_000) })
    .optional(),
  answerField: z.string().max(64).optional(),
});

export const AgentLimitsSchema = z.object({
  maxSteps: z.number().int().min(1).max(AGENT_LIMITS.maxSteps).default(AGENT_LIMITS.defaultSteps),
  maxCostUsd: z
    .number()
    .min(AGENT_LIMITS.minCostUsd)
    .max(AGENT_LIMITS.maxCostUsd)
    .nullable()
    .default(AGENT_LIMITS.defaultCostUsd),
  maxActiveSec: z
    .number()
    .int()
    .min(AGENT_LIMITS.minActiveSec)
    .max(AGENT_LIMITS.maxActiveSec)
    .default(AGENT_LIMITS.defaultActiveSec),
});

export const AgentConfigSchema = z
  .object({
    // Optional in the shape: a new agent is saved before one is picked ("Pick a model…").
    credentialId: id.optional(),
    model: z.string().trim().min(1).max(100).optional(),
    maxTokens: z.number().int().positive().max(64_000).default(8192),
    temperature: z.number().min(0).max(2).optional(),
    thinking: z.enum(['adaptive', 'off']).default('adaptive'),
    messages: PromptMessagesSchema.optional(),
    library: LibraryPromptPinSchema.optional(),
    tools: z
      .array(AgentToolSchema)
      .max(AGENT_LIMITS.maxTools)
      .default([])
      .superRefine((tools, ctx) => {
        const keys = new Set<string>();
        const names = new Set<string>();
        tools.forEach((tool, index) => {
          if (keys.has(tool.key)) {
            ctx.addIssue({
              code: 'custom',
              path: [index, 'key'],
              message: 'this key is used twice',
            });
          }
          if (names.has(tool.name)) {
            ctx.addIssue({
              code: 'custom',
              path: [index, 'name'],
              message: `two tools are named "${tool.name}"`,
            });
          }
          keys.add(tool.key);
          names.add(tool.name);
        });
      }),
    memory: AgentMemorySchema.default({ mode: 'off' }),
    limits: AgentLimitsSchema.default({
      maxSteps: AGENT_LIMITS.defaultSteps,
      maxCostUsd: AGENT_LIMITS.defaultCostUsd,
      maxActiveSec: AGENT_LIMITS.defaultActiveSec,
    }),
    onBudgetExhausted: z.enum(['hold', 'limit']).default('hold'),
    output: AgentOutputSchema.default({ kind: 'text' }),
    toolResultMaxChars: z
      .number()
      .int()
      .min(500)
      .max(200_000)
      .default(AGENT_LIMITS.defaultToolResultChars),
  })
  .superRefine((config, ctx) => {
    if (config.messages !== undefined && config.library !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['messages'],
        message: 'either messages in the step or a Library prompt, not both',
      });
    } else if (config.messages === undefined && config.library === undefined) {
      ctx.addIssue({ code: 'custom', path: ['messages'], message: 'write the instructions' });
    }
  });

export type TAgentConfig = z.output<typeof AgentConfigSchema>;
export type TAgentTool = z.output<typeof AgentToolSchema>;
export type TAgentToolField = z.output<typeof AgentToolFieldSchema>;
export type TAgentOutput = z.output<typeof AgentOutputSchema>;
export type TAgentOutputField = z.output<typeof AgentOutputFieldSchema>;
